import { cloneWithNewIds, createId, createNode, createRow, createSection, type Node, type NodeLocation, type RegionId, type LibraryItem } from "@broadpaper/core";
import type { FieldDragPayload } from "./session.js";
import { documentToHtml, printReport, createPdfClient, downloadBlob } from "@broadpaper/renderer";
import type { EditorSession } from "./session.js";

const CLIPBOARD_MIME = "application/x-broadpaper-nodes+json";
let memoryClipboard: Node[] | null = null;

export function createActions(session: EditorSession) {
  const store = session.store;

  const selectedEntries = () => {
    const idx = store.index();
    return store.getState().selection.map((id) => idx.get(id)).filter((e): e is NonNullable<typeof e> => !!e);
  };

  /** Where a new block should go for the current selection: after the selected block, or at the end of the last section. */
  /** Forgets the caret: once something is inserted its position is stale. */
  const clearInsertion = () => {
    if (session.ui.get().insertion) session.ui.patch({ insertion: null });
  };

  const insertionPoint = (): NodeLocation => {
    // A caret placed by clicking wins over the selection: it is the more
    // specific statement of intent, and the more recent one.
    const caret = session.ui.get().insertion;
    if (caret) {
      const parentPath = caret.parentId ? store.locationOf(caret.parentId)?.path : undefined;
      if (!caret.parentId) return { region: caret.region, path: [caret.index] };
      if (parentPath) return { region: caret.region, path: [...parentPath, caret.index] };
    }
    const sel = selectedEntries()[0];
    if (sel) {
      const def = session.registry.get(sel.node.type);
      if (def?.container && sel.node.type !== "row") return { region: sel.region, path: [...sel.path, sel.node.children?.length ?? 0] };
      return { region: sel.region, path: [...sel.path.slice(0, -1), sel.index + 1] };
    }
    const body = store.template.body;
    if (body.length === 0) return { region: "body", path: [0] };
    const last = body[body.length - 1]!;
    return { region: "body", path: [body.length - 1, last.children?.length ?? 0] };
  };

  const ensureSection = (region: RegionId) => {
    const t = store.template;
    const list = region === "body" ? t.body : region === "header" ? t.header.default : region === "header:first" ? t.header.firstPage ?? [] : region === "footer" ? t.footer.default : t.footer.firstPage ?? [];
    if (list.length === 0) store.dispatch({ type: "insertNode", node: createSection(), at: { region, path: [0] } }, { label: "Add section" });
  };

  const actions = {
    insertionPoint,

    insertBlock(type: string, at?: NodeLocation, props?: Record<string, unknown>): string | undefined {
      const def = session.registry.get(type);
      if (!def) return undefined;
      const node = createNode(session.registry, type, { props });
      const target = at ?? insertionPoint();
      store.transaction(`Add ${def.name}`, () => {
        if (target.path.length === 1 && target.region) {
          // Inserting at region root: wrap non-sections into a section.
          if (type !== "section") {
            ensureSection(target.region);
            const list = target.region === "body" ? store.template.body : [];
            const sectionIndex = Math.min(target.path[0]!, Math.max(0, list.length - 1));
            const section = list[sectionIndex];
            store.dispatch({ type: "insertNode", node, at: { region: target.region, path: [sectionIndex, section?.children?.length ?? 0] } });
            return;
          }
        }
        store.dispatch({ type: "insertNode", node, at: target });
      });
      store.select([node.id]);
      clearInsertion();
      session.reveal(node.id);
      return node.id;
    },

    /**
     * Puts a data field on the page.
     *
     * Shared with the drop handler on purpose: clicking a field in the data
     * panel and dragging it onto the page have to produce the same block, and
     * they did not when each built its own props. The block itself decides what
     * to build, through its `fromField` hook, so a list becomes a finished
     * table and a custom block can answer for itself.
     */
    insertField(field: FieldDragPayload, at?: NodeLocation): string | undefined {
      const type = field.type === "array" ? "table" : "field";
      const def = session.registry.get(type);
      const props = def?.fromField?.({
        expr: field.expr,
        formatted: field.bound ?? field.expr,
        schema: field.schema ?? { type: (field.type ?? "string") as never },
        label: field.label
      });
      const leaf = field.label.split(" › ").pop() ?? field.label;
      const fallback = type === "table" ? { source: field.expr } : { label: leaf, value: field.bound ?? field.expr };
      return this.insertBlock(type, at, (props as Record<string, unknown>) ?? fallback);
    },

    insertRow(layout: number[], at?: NodeLocation): string {
      const row = createRow(layout);
      store.transaction("Add columns", () => store.dispatch({ type: "insertNode", node: row, at: at ?? insertionPoint() }));
      store.select([row.id]);
      clearInsertion();
      session.reveal(row.id);
      return row.id;
    },

    addSection(region: RegionId = "body", index?: number): string {
      const section = createSection();
      const list = region === "body" ? store.template.body : [];
      store.dispatch({ type: "insertNode", node: section, at: { region, path: [index ?? list.length] } }, { label: "Add section" });
      store.select([section.id]);
      session.reveal(section.id);
      return section.id;
    },

    insertNodeAt(node: Node, at: NodeLocation, label = "Insert"): void {
      store.transaction(label, () => {
        if (at.path.length === 1 && node.type !== "section") {
          ensureSection(at.region);
          const list = at.region === "body" ? store.template.body : at.region === "header" ? store.template.header.default : at.region === "footer" ? store.template.footer.default : [];
          const si = Math.min(at.path[0]!, Math.max(0, list.length - 1));
          store.dispatch({ type: "insertNode", node, at: { region: at.region, path: [si, list[si]?.children?.length ?? 0] } });
        } else store.dispatch({ type: "insertNode", node, at });
      });
      store.select([node.id]);
      clearInsertion();
      session.reveal(node.id);
    },

    moveNode(id: string, to: NodeLocation): void {
      const entry = store.index().get(id);
      if (!entry || entry.node.locked) return;
      store.dispatch({ type: "moveNode", id, to }, { label: "Move" });
      store.select([id]);
    },

    deleteSelection(): void {
      const ids = store.getState().selection.filter((id) => !store.index().get(id)?.node.locked);
      if (ids.length === 0) return;
      // Select the neighbour so the user can keep working with the keyboard.
      const first = store.index().get(ids[0]!);
      const parentId = first?.parent?.id ?? null;
      const siblings = first ? (first.parent ? first.parent.children ?? [] : []) : [];
      const neighbour = siblings[first!.index + 1] ?? siblings[first!.index - 1];
      store.dispatch({ type: "removeNodes", ids }, { label: ids.length > 1 ? `Delete ${ids.length} blocks` : "Delete" });
      store.select(neighbour && !ids.includes(neighbour.id) ? [neighbour.id] : parentId ? [parentId] : []);
    },

    duplicateSelection(): void {
      const entries = selectedEntries();
      if (!entries.length) return;
      const ids: string[] = [];
      store.transaction("Duplicate", () => {
        for (const e of entries) {
          const newId = createId(e.node.type === "section" ? "sec" : e.node.type === "row" ? "row" : "n");
          store.dispatch({ type: "duplicateNode", id: e.node.id, newId });
          ids.push(newId);
        }
      });
      store.select(ids);
    },

    async copySelection(): Promise<void> {
      const nodes = selectedEntries().map((e) => structuredClone(e.node));
      if (!nodes.length) return;
      memoryClipboard = nodes;
      try {
        const payload = JSON.stringify({ broadpaper: 1, nodes });
        if (navigator.clipboard && "write" in navigator.clipboard && typeof ClipboardItem !== "undefined") {
          await navigator.clipboard.write([new ClipboardItem({ "text/plain": new Blob([payload], { type: "text/plain" }), [CLIPBOARD_MIME]: new Blob([payload], { type: CLIPBOARD_MIME }) })]).catch(() => navigator.clipboard.writeText(payload));
        } else await navigator.clipboard?.writeText(payload);
      } catch {
        /* clipboard may be unavailable; memory clipboard still works */
      }
      session.toast(nodes.length === 1 ? "Copied block" : `Copied ${nodes.length} blocks`);
    },

    async cutSelection(): Promise<void> {
      await actions.copySelection();
      actions.deleteSelection();
    },

    async pasteFromClipboard(text?: string): Promise<void> {
      let nodes: Node[] | null = null;
      const raw = text ?? (await navigator.clipboard?.readText?.().catch(() => "")) ?? "";
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as { broadpaper?: number; nodes?: Node[] };
          if (parsed.broadpaper === 1 && Array.isArray(parsed.nodes)) nodes = parsed.nodes;
        } catch {
          /* not ours */
        }
      }
      nodes ??= memoryClipboard;
      if (!nodes?.length) return;
      actions.pasteNodes(nodes);
    },

    pasteNodes(nodes: Node[]): void {
      const copies = nodes.map(cloneWithNewIds);
      const ids: string[] = [];
      store.transaction(copies.length > 1 ? `Paste ${copies.length} blocks` : "Paste", () => {
        let at = insertionPoint();
        for (const n of copies) {
          if (n.type === "section") at = { region: at.region, path: [at.path[0]! + 1] };
          else if (at.path.length === 1) {
            ensureSection(at.region);
            const list = store.template.body;
            const si = Math.min(at.path[0]!, list.length - 1);
            at = { region: at.region, path: [si, list[si]?.children?.length ?? 0] };
          }
          store.dispatch({ type: "insertNode", node: n, at });
          ids.push(n.id);
          at = { region: at.region, path: [...at.path.slice(0, -1), at.path[at.path.length - 1]! + 1] };
        }
      });
      store.select(ids);
      // Library items and pasted blocks land wherever the caret is, which may
      // be a page away from what you are looking at.
      if (ids[0]) session.reveal(ids[0]);
    },

    moveSelection(direction: -1 | 1): void {
      const entry = selectedEntries()[0];
      if (!entry || entry.node.locked) return;
      const list = entry.parent ? entry.parent.children ?? [] : entry.region === "body" ? store.template.body : [];
      const target = entry.index + direction;
      if (target < 0 || target >= list.length) return;
      store.dispatch({ type: "moveNode", id: entry.node.id, to: { region: entry.region, path: [...entry.path.slice(0, -1), direction > 0 ? target + 1 : target] } }, { label: direction > 0 ? "Move down" : "Move up" });
      store.select([entry.node.id]);
    },

    selectParent(): void {
      const entry = selectedEntries()[0];
      if (entry?.parent) store.select([entry.parent.id]);
    },

    selectSibling(direction: -1 | 1): void {
      const entry = selectedEntries()[0];
      if (!entry) {
        const first = store.template.body[0]?.children?.[0] ?? store.template.body[0];
        if (first) store.select([first.id]);
        return;
      }
      const list = entry.parent ? entry.parent.children ?? [] : store.template.body;
      const next = list[entry.index + direction];
      if (next) {
        store.select([next.id]);
        session.reveal(next.id);
      }
    },

    selectFirstChild(): void {
      const entry = selectedEntries()[0];
      const child = entry?.node.children?.[0];
      if (child) store.select([child.id]);
    },

    wrapSelectionIn(type: "container" | "card" | "keepTogether" | "conditional"): void {
      const entries = selectedEntries().filter((e) => e.node.type !== "section" && e.node.type !== "column");
      if (!entries.length) return;
      const wrapper = createNode(session.registry, type);
      store.dispatch({ type: "wrapNodes", ids: entries.map((e) => e.node.id), wrapper }, { label: `Wrap in ${session.registry.get(type)?.name ?? type}` });
      store.select([wrapper.id]);
    },

    saveSelectionToLibrary(name: string, description?: string): void {
      const entry = selectedEntries()[0];
      if (!entry) return;
      const item: LibraryItem = { id: createId("lib"), name, description, node: cloneWithNewIds(entry.node) };
      store.dispatch({ type: "addLibraryItem", item }, { label: "Save to library" });
      session.toast(`Saved "${name}" to the library`, "success");
    },

    toggleLock(): void {
      const entry = selectedEntries()[0];
      if (!entry) return;
      store.dispatch({ type: "setLocked", id: entry.node.id, locked: !entry.node.locked });
    },

    undo(): void {
      if (store.undo()) session.toast(`Undo ${store.redoLabel() ?? ""}`.trim());
    },
    redo(): void {
      if (store.redo()) session.toast(`Redo ${store.undoLabel() ?? ""}`.trim());
    },

    async save(): Promise<void> {
      const o = session.options.get();
      if (!o.onSave) return;
      session.ui.patch({ save: { status: "saving" } });
      try {
        await o.onSave(store.template);
        store.markClean();
        session.ui.patch({ save: { status: "saved", at: Date.now() } });
      } catch (e) {
        // No toast: the message belongs next to the button that failed, where
        // it stays until the next attempt, rather than disappearing in 2.6s.
        session.ui.patch({ save: { status: "error", message: (e as Error).message } });
      }
    },

    print(): void {
      const layout = session.layout.get();
      if (!layout) return;
      const state = store.getState();
      // Print always uses preview mode with real data.
      if (state.mode !== "preview") {
        session.toast("Switch to Preview to print with data", "info");
      }
      // Asynchronous because it checks the licence before writing the page —
      // "Save as PDF" in the browser's dialog produces a PDF like any other
      // export and is marked on the same terms. Nothing here waits for the
      // dialog, so the promise is deliberately dropped; a failure to open it
      // is reported by the browser, not by us.
      void printReport(layout, { title: store.template.name });
    },

    previewHtml(): string | null {
      const layout = session.layout.get();
      return layout ? documentToHtml(layout, { title: store.template.name }) : null;
    },

    async exportPdf(): Promise<void> {
      const o = session.options.get();
      const layout = session.layout.get();
      const theme = session.activeTheme;
      const data = session.activeSample;
      if (o.onExportPdf) {
        const handled = await o.onExportPdf({ template: store.template, theme, data, dataSources: session.dataSources, layout, print: () => actions.print() });
        if (handled !== false) return;
      }
      if (o.pdfService?.url) {
        session.ui.patch({ exporting: true });
        try {
          const client = createPdfClient({ baseUrl: o.pdfService.url, token: o.pdfService.token });
          const { blob, pages } = await client.render({ template: store.template, data, theme, dataSources: session.dataSources, locale: o.locale, currency: o.currency, timeZone: o.timeZone, metadata: { title: store.template.name } });
          downloadBlob(blob, `${store.template.name.replace(/[^\w.-]+/g, "_") || "report"}.pdf`);
          session.toast(`Exported ${pages} page${pages === 1 ? "" : "s"}`, "success");
        } catch (e) {
          session.toast(`PDF export failed: ${(e as Error).message}`, "error");
        } finally {
          session.ui.patch({ exporting: false });
        }
        return;
      }
      actions.print();
    }
  };
  return actions;
}

export type EditorActions = ReturnType<typeof createActions>;
